<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" :isClosable="!isSaving" :isCloseButtonVisible="!isSaving">
    <template #content>
      <div
        :style="{
          opacity: isSaving ? '0.5' : '1',
          pointerEvents: isSaving ? 'none' : 'auto',
        }"
      >
        <div style="margin-bottom: 12px">
          <h1 class="global-h1" style="margin-bottom: 5px">
            {{ isCreateMode ? "Generate a Social Post" : "Edit Post Guidance" }}
          </h1>
          <p style="color: #666; font-size: 14px; line-height: 1.5; margin-top: 10px">
            Describe the kind of post you want - theme, vibe, key details.
          </p>
        </div>

        <input
          :class="{
            'global-text-input': true,
            'global-text-input--error': !isFormValid && !_title,
          }"
          v-model="_title"
          style="max-width: 300px; margin-bottom: 12px"
          placeholder="Theme/Purpose"
          type="text"
          @focus="isFormValid = true"
        />

        <textarea
          :class="{
            'global-textarea': true,
            'global-textarea--error': !isFormValid && !_description,
          }"
          v-model="_description"
          style="height: 85px; min-height: 85px"
          placeholder="Guidance for the AI: What should the post be about?"
          @focus="isFormValid = true"
        ></textarea>

        <input v-if="props.itemToEdit && _specificDate" class="global-text-input" v-model="_specificDate" style="max-width: 300px; margin-top: 12px" placeholder="Timing (optional)" type="text" />

        <div style="margin-top: 12px">
          <h4 style="margin-bottom: 8px">Content Album</h4>
          <div v-if="!selectedContentSummary">
            We'll find a content album to match with, or you can:<br/>
            <LinkWithIcon  isDottedUnderline @click="isPickContentModalVisible = true" style="margin-top: 8px">
              <template #icon>
                <IconEmbedded name="folder_2" :size="19" />
              </template>
              <span>Manually choose one</span>
            </LinkWithIcon>
          </div>
          <div v-else style="margin-top: 8px">
            <ContentSummary
              :contentSummaryData="selectedContentSummary"
              :isUnbindButtonVisible="false"
              :isContentSourcedFromLabelVisible="false"
              :hideDecorations="true"
              :customerId="props.customerId"
              :isRemoveButtonVisible="true"
              @clickViewPhotos="handleViewPhotos"
              @clickRemove="selectedContentSummary = null"
            />
          </div>
        </div>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <div style="gap: 12px; display: flex">
        <SrpButton
          fill="outlined"
          color="gray"
          @click="
            () => {
              emit('cancel');
              closeModal();
            }
          "
          :isDisabled="isSaving"
        >
          Cancel
        </SrpButton>

        <SrpButton
          @click="
            async () => {
              if (!_title || !_description) {
                isFormValid = false;
                return;
              }
              await save();
              closeModal();
            }
          "
          :isDisabled="isSaving"
        >
          <template #icon>
            <LoadingIcon v-if="isSaving" :size="20" />
            <IconEmbedded v-else :name="isCreateMode ? 'ai_2' : 'save_2-5'" :size="20" />
          </template>
          {{ isCreateMode ? (isSaving ? "Generating" : "Generate") : isSaving ? "Updating" : "Update" }}
        </SrpButton>
      </div>
    </template>
  </SrpModal>

  <PickFromExistingContentModal
    v-if="isPickContentModalVisible"
    v-model:isVisible="isPickContentModalVisible"
    :customerId="props.customerId"
    :calendarItem="null"
    @close="isPickContentModalVisible = false"
    @picked="contentSummary => handleContentSummarySelected(contentSummary)"
  />

  <ViewAllPhotosModal
    v-if="isViewAllPhotosModalVisible"
    v-model:isVisible="isViewAllPhotosModalVisible"
    :customerId="props.customerId"
    :contentSummaryId="viewAllPhotosSummaryId"
  />
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import { useToastsStore } from "@stores/toasts";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import PickFromExistingContentModal from "@views/Community/ContentCalendar/steps/modals/PickFromExistingContentModal.vue";
import ContentSummary from "@views/Community/ContentCalendar/eventSnippets/ContentSummary.vue";
import ViewAllPhotosModal from "@views/Community/ContentCalendar/steps/modals/ViewAllPhotosModal.vue";

// Types
import { ContentCalendarItem, ContentSummaryPreview } from "@contracts/contentCalendar";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    itemToEdit?: ContentCalendarItem;
    customerId: string;
    weekGroup?: string | null;
    subTitle?: string;
  }>(),
  {
    itemToEdit: null,
    weekGroup: null,
    subTitle: "",
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "save", item: ContentCalendarItem): void;
  (e: "create", item: ContentCalendarItem): void;
  (e: "cancel"): void;
}>();

// Local values ===============================================================
const _title = ref<string>("");
const _description = ref<string>("");
const _specificDate = ref<string>("");

// Content group selection ====================================================
const selectedContentSummary = ref<ContentSummaryPreview | null>(null);
const isPickContentModalVisible = ref<boolean>(false);

function handleContentSummarySelected(contentSummary: ContentSummaryPreview) {
  selectedContentSummary.value = contentSummary;
  isPickContentModalVisible.value = false;
}

// View all photos modal ======================================================
const isViewAllPhotosModalVisible = ref<boolean>(false);
const viewAllPhotosSummaryId = ref<string>("");

function handleViewPhotos() {
  viewAllPhotosSummaryId.value = selectedContentSummary.value?.id || "";
  isViewAllPhotosModalVisible.value = true;
}

onMounted(() => {
  _title.value = props.itemToEdit?.title ?? "";
  _description.value = props.itemToEdit?.description ?? "";
});

// Is Create Mode =============================================================
const isCreateMode = computed<boolean>(() => !props.itemToEdit);

// Validate form ==============================================================
const isFormValid = ref<boolean>(true);

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save() {
  isSaving.value = true;

  if (props.itemToEdit) {
    const updatedItem = await ContentCalendarRepo.updateItem(props.customerId, {
      ...props.itemToEdit,
      title: _title.value,
      description: _description.value,
    });

    emit("save", updatedItem);
  } else {
    const response = await ContentCalendarRepo.matchAndGeneratePost(props.customerId, {
      title: _title.value,
      description: _description.value,
      weekGroup: props.weekGroup,
      contentSourceType: selectedContentSummary.value?.contextType,
      sourceEntityId: selectedContentSummary.value?.contextId,
    });

    if (response.failedToMatch) {
      useToastsStore().warn({
        message: "Couldn't find suitable content",
        expiresInMs: 8000,
        details: "The guidance was created but we couldn't match it to any of your existing content.",
      });
    }

    emit("create", response.calendarItem);
  }

  isSaving.value = false;
}
</script>

<style scoped lang="scss"></style>
